/**
 * Theme Context
 * 
 * Provides theme state and methods throughout the application.
 * Handles theme switching, localStorage persistence, and CSS variable injection.
 */

/* eslint-disable react-refresh/only-export-components */
// Note: Fast refresh rule disabled because Context files need to export both
// the Provider component and the Context object. This only affects HMR during
// development and has no impact on production builds.

import React, { createContext, useState, useEffect, useCallback, useContext, type ReactNode } from 'react';
import { 
  themes, 
  getThemeCSSVariables, 
  DEFAULT_THEME, 
  THEME_STORAGE_KEY,
  type ThemeName,
  type ThemeConfig
} from '../config/themes';

// =============================================================================
// Types
// =============================================================================

export interface ThemeContextType {
  /** Current theme name */
  theme: ThemeName;
  /** Current theme configuration */
  themeConfig: ThemeConfig;
  /** Change the current theme */
  setTheme: (theme: ThemeName) => void;
  /** Toggle between themes */
  toggleTheme: () => void;
  /** All available themes */
  availableThemes: typeof themes;
  /** Check if a specific theme is active */
  isTheme: (theme: ThemeName) => boolean;
}

// =============================================================================
// Context
// =============================================================================

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the saved theme from localStorage, with validation
 */
function getSavedTheme(): ThemeName {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved && (saved === 'original' || saved === 'loop' || saved === 'loop-full')) {
      return saved as ThemeName;
    }
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error);
  }
  return DEFAULT_THEME;
}

/**
 * Apply theme CSS variables to the document root
 */
function applyThemeToDOM(themeConfig: ThemeConfig): void {
  const root = document.documentElement;
  const cssVariables = getThemeCSSVariables(themeConfig);
  
  // Apply all CSS variables
  Object.entries(cssVariables).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
  
  // Apply font family to body
  document.body.style.fontFamily = themeConfig.fontFamily;
  
  // Apply heading font family
  const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
  headings.forEach((heading) => {
    (heading as HTMLElement).style.fontFamily = themeConfig.headingFontFamily;
  });
  
  // Add theme class to body for CSS targeting
  document.body.classList.remove('theme-original', 'theme-loop', 'theme-loop-full');
  document.body.classList.add(`theme-${themeConfig.name}`);
  
  // Set data attribute for CSS selectors
  document.documentElement.setAttribute('data-theme', themeConfig.name);
}

// =============================================================================
// Provider Component
// =============================================================================

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeName>(() => getSavedTheme());

  // Get current theme configuration
  const themeConfig = themes[theme];

  // ---------------------------------------------------------------------------
  // Theme Application
  // ---------------------------------------------------------------------------

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyThemeToDOM(themeConfig);
  }, [themeConfig]);

  // ---------------------------------------------------------------------------
  // Theme Change Handler
  // ---------------------------------------------------------------------------

  const setTheme = useCallback((newTheme: ThemeName) => {
    if (newTheme === theme) return;
    
    // Add transition class for smooth change
    document.body.classList.add('theme-transitioning');
    
    // Save to localStorage
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
    
    // Update state
    setThemeState(newTheme);
    
    // Remove transition class after animation
    setTimeout(() => {
      document.body.classList.remove('theme-transitioning');
    }, 300);
  }, [theme]);

  // ---------------------------------------------------------------------------
  // Toggle Theme
  // ---------------------------------------------------------------------------

  const toggleTheme = useCallback(() => {
    const newTheme: ThemeName = theme === 'original' ? 'loop' : 'original';
    setTheme(newTheme);
  }, [theme, setTheme]);

  // ---------------------------------------------------------------------------
  // Check Theme
  // ---------------------------------------------------------------------------

  const isTheme = useCallback((checkTheme: ThemeName): boolean => {
    return theme === checkTheme;
  }, [theme]);

  // ---------------------------------------------------------------------------
  // Context Value
  // ---------------------------------------------------------------------------

  const value: ThemeContextType = {
    theme,
    themeConfig,
    setTheme,
    toggleTheme,
    availableThemes: themes,
    isTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access theme context
 * @throws Error if used outside of ThemeProvider
 */
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Re-export types and utilities
export { type ThemeName, type ThemeConfig } from '../config/themes';
